// Auth view — editorial two-pane layout. Email + password, OAuth (Apple, Microsoft,
// Google), magic-link fallback. Locked: only authenticated users see the dashboard.
import { supabase } from "./supabase";

type Provider = "apple" | "azure" | "google";

export function renderAuth(root: HTMLElement, onSignIn: () => void) {
  root.innerHTML = `
    <div class="auth-shell">
      <aside class="auth-stage">
        <div class="auth-stage-grain"></div>
        <div class="auth-stage-inner">
          <div class="auth-crest">A</div>
          <div class="auth-house">
            <div class="auth-house-line"></div>
            <div class="auth-house-name">Biogenix</div>
            <div class="auth-house-line"></div>
          </div>
          <blockquote class="auth-quote">
            <span class="auth-quote-mark">&ldquo;</span>
            Quietly, in the background — the right thing,
            at the right moment, on your behalf.
          </blockquote>
          <div class="auth-stage-foot">
            <div>Alfred · executive assistant</div>
            <div class="auth-stage-foot-r">v0.3 · cockpit</div>
          </div>
        </div>
      </aside>

      <main class="auth-pane">
        <div class="auth-form">
          <div class="auth-eyebrow">Private access</div>
          <h1 class="auth-h1">Good <em>morning</em>.</h1>
          <p class="auth-p">Sign in to your assistant.</p>

          <div class="auth-oauth">
            <button class="oauth-btn" data-provider="apple" type="button">
              ${oauthIcon("apple")}
              <span>Continue with Apple</span>
            </button>
            <button class="oauth-btn" data-provider="azure" type="button">
              ${oauthIcon("microsoft")}
              <span>Continue with Microsoft</span>
            </button>
            <button class="oauth-btn" data-provider="google" type="button">
              ${oauthIcon("google")}
              <span>Continue with Google</span>
            </button>
          </div>

          <div class="auth-divider"><span>or with email</span></div>

          <form id="auth-form" novalidate>
            <label class="auth-field">
              <span class="auth-field-label">Email</span>
              <input type="email" id="auth-email" autocomplete="email"
                     placeholder="dominik@biogenix.com.au" required />
            </label>

            <label class="auth-field">
              <span class="auth-field-label">Password</span>
              <input type="password" id="auth-password" autocomplete="current-password"
                     placeholder="•••••••••••" required />
            </label>

            <div class="auth-row">
              <label class="auth-check">
                <input type="checkbox" id="auth-remember" checked />
                <span>Remember this device</span>
              </label>
              <button type="button" class="auth-link" id="auth-magic">Email me a link instead</button>
            </div>

            <button class="auth-submit" id="auth-submit" type="submit">Sign in</button>

            <div class="auth-status" id="auth-status"></div>
          </form>

          <footer class="auth-foot">
            <span>Biogenix Australia</span>
            <span class="auth-foot-dot">·</span>
            <span>Encrypted · Australian Privacy Principles</span>
          </footer>
        </div>
      </main>
    </div>
  `;

  const emailIn = root.querySelector<HTMLInputElement>("#auth-email")!;
  const passIn = root.querySelector<HTMLInputElement>("#auth-password")!;
  const form = root.querySelector<HTMLFormElement>("#auth-form")!;
  const submit = root.querySelector<HTMLButtonElement>("#auth-submit")!;
  const status = root.querySelector<HTMLDivElement>("#auth-status")!;
  const magicBtn = root.querySelector<HTMLButtonElement>("#auth-magic")!;

  emailIn.focus();

  const setStatus = (msg: string, cls: "" | "error" | "success" | "info" = "") => {
    status.textContent = msg;
    status.className = "auth-status" + (cls ? " " + cls : "");
  };

  // OAuth providers
  root.querySelectorAll<HTMLButtonElement>(".oauth-btn").forEach(btn => {
    btn.addEventListener("click", async () => {
      const provider = btn.dataset.provider as Provider;
      btn.disabled = true;
      setStatus(`Redirecting to ${provider === "azure" ? "Microsoft" : provider}...`, "info");
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: { redirectTo: window.location.origin },
      });
      if (error) {
        setStatus(error.message, "error");
        btn.disabled = false;
      }
    });
  });

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const email = emailIn.value.trim();
    const password = passIn.value;
    if (!email || !email.includes("@")) { setStatus("Enter a valid email.", "error"); return; }
    if (!password) { setStatus("Enter your password.", "error"); return; }
    submit.disabled = true;
    setStatus("Signing in...", "info");
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      setStatus(error.message, "error");
      submit.disabled = false;
      return;
    }
    setStatus("Welcome.", "success");
  });

  magicBtn.addEventListener("click", async () => {
    const email = emailIn.value.trim();
    if (!email || !email.includes("@")) { setStatus("Enter your email above first.", "error"); return; }
    setStatus("Sending magic link...", "info");
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.origin },
    });
    if (error) { setStatus(error.message, "error"); return; }
    setStatus("Magic link sent — check your inbox.", "success");
  });

  supabase.auth.onAuthStateChange((event, session) => {
    if ((event === "SIGNED_IN" || event === "INITIAL_SESSION") && session) onSignIn();
  });
  supabase.auth.getSession().then(({ data }) => {
    if (data.session) onSignIn();
  });
}


// ── OAuth provider logos (inline SVG, brand-accurate) ────────────────────
function oauthIcon(kind: "apple" | "microsoft" | "google"): string {
  switch (kind) {
    case "apple":
      return `<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M17.05 12.04c-.03-2.94 2.4-4.36 2.51-4.43-1.37-2-3.51-2.28-4.27-2.31-1.81-.19-3.55 1.08-4.47 1.08-.93 0-2.36-1.06-3.88-1.03-2 .03-3.85 1.16-4.88 2.94-2.08 3.6-.53 8.93 1.5 11.86 1 1.43 2.19 3.04 3.77 2.98 1.52-.06 2.09-.98 3.93-.98 1.82 0 2.36.98 3.97.95 1.64-.03 2.68-1.46 3.68-2.9 1.16-1.66 1.64-3.27 1.67-3.36-.04-.02-3.2-1.23-3.23-4.87z"/><path d="M14.31 3.99c.82-1 1.38-2.39 1.22-3.78-1.18.05-2.61.79-3.46 1.78-.76.88-1.43 2.29-1.25 3.65 1.31.1 2.66-.67 3.49-1.65z"/></svg>`;
    case "microsoft":
      return `<svg viewBox="0 0 24 24" width="18" height="18" fill="none"><rect x="2" y="2" width="9" height="9" fill="#f25022"/><rect x="13" y="2" width="9" height="9" fill="#7fba00"/><rect x="2" y="13" width="9" height="9" fill="#00a4ef"/><rect x="13" y="13" width="9" height="9" fill="#ffb900"/></svg>`;
    case "google":
      return `<svg viewBox="0 0 24 24" width="18" height="18"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.15-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.85 14.1c-.22-.66-.34-1.36-.34-2.1s.12-1.44.34-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.67-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.67 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>`;
  }
}
